import React, { Component } from "react";
import { addMinutes, subMinutes } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { duration } from "@formant/core/model/duration";
import { IStreamData } from "@formant/core/model/IStreamData";
import { ITags } from "@formant/core/model/ITags";
import { IViewConfiguration } from "@formant/core/model/IViewConfiguration";
import { Uuid } from "@formant/core/model/Uuid";

import { observeStore, organizationStore, queryStore } from "../../../stores";
import { DeviceName } from "../../common/DeviceName";
import { DeviceSelector } from "../../common/DeviceSelector";
import { Table } from "../../common/Table";
import { Timer } from "../../common/Timer";
import { IBaseModuleProps } from "../IBaseModuleProps";
import { Module, ModuleSize } from "../Module";
import { ModuleFooter } from "../ModuleFooter";
import { pointsByTagValue } from "../utils/pointsByTagValue";
import { BitsetModuleRow } from "./BitsetModuleRow";
import * as styles from "./index.scss";

@observer
export class BitsetModule extends Component<IBaseModuleProps> {
    @observable
    private selectedDeviceId?: Uuid;
    @observable.shallow
    private visibleKeys = new Set<string>();
    @observable
    private size?: ModuleSize;
    @observable
    private columnWidth = 100;
    @observable
    private dirtyResize = false;
    private initialized = false;
    private data: IStreamData<"bitset">[] = [];
    private table?: Table;
    private mutationObserver: MutationObserver;
    private headerRef = React.createRef<HTMLDivElement>();

    constructor(props: IBaseModuleProps) {
        super(props);
        this.mutationObserver = new MutationObserver(() => {
            this.handleResize();
        });
    }

    public componentDidMount() {
        if (this.headerRef.current) {
            this.mutationObserver.observe(this.headerRef.current, {
                attributes: true,
            });
            const streamConfig = this.props.configuration.find(
                config => config.streamName === this.props.streamName,
            ) as IViewConfiguration<"bitset"> | undefined;
            if (streamConfig && streamConfig.configuration.columnWidth) {
                this.columnWidth = streamConfig.configuration.columnWidth;
            } else {
                this.columnWidth = this.headerRef.current.offsetWidth;
            }
        }
    }

    public render() {
        const {
            streamName,
            filter,
            focused,
            hovering,
            fullscreen,
            onSelectTime,
        } = this.props;
        const { time, mode } = observeStore;

        const live = mode === "live";

        const startTime = subMinutes(time, 2);
        const endTime = addMinutes(time, 1);

        const queryData = queryStore.moduleQuery(
            filter,
            streamName,
            "bitset",
            startTime,
            endTime,
        );
        const loading = queryData === undefined;
        const tooMuchData = queryData === "too much data";

        this.data = (queryData !== "too much data" && queryData) || this.data;
        const data = this.data;

        const empty = data.length === 0;

        const { metadata, selectedDeviceId } = this;

        const deviceIds = metadata.map(_ => _.deviceId);

        const selected = metadata.find(_ => _.deviceId === selectedDeviceId);

        const defaultStreamThrottleHz =
            organizationStore.defaultStreamThrottleHz;

        const rows =
            selected &&
            selected.keys.map((rowData, i) => (
                <BitsetModuleRow
                    defaultStreamThrottleHz={defaultStreamThrottleHz}
                    key={i}
                    data={rowData}
                    deviceId={selected.deviceId}
                    visible={this.visibleKeys.has(rowData.key)}
                    live={live}
                    time={time}
                    startTime={startTime}
                    endTime={endTime}
                    size={this.size}
                    onSelectTime={onSelectTime}
                    columnWidth={this.columnWidth}
                />
            ));

        return (
            <Module
                {...this.props}
                type="bitset"
                loading={loading}
                empty={empty}
                tooMuchData={tooMuchData}
                className={styles["bitset-module"]}
                onChangeSize={this.onChangeSize}
                preventContentDrag={true}
            >
                <Timer interval={duration.second} onTick={this.onTick} />
                <div className={styles.content}>
                    <Table
                        className={styles.table}
                        disableScrolling={!focused}
                        ref={_ => (this.table = _ || undefined)}
                    >
                        <thead>
                            <tr>
                                <th />
                                <th className={styles.tableHeader}>
                                    <div
                                        ref={this.headerRef}
                                        className={styles.resizableHeader}
                                        style={
                                            this.dirtyResize
                                                ? {}
                                                : { width: this.columnWidth }
                                        }
                                    >
                                        Key
                                    </div>
                                </th>
                                <th className={styles.tableHeader}>
                                    <div>Value</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>{rows}</tbody>
                    </Table>
                </div>
                <ModuleFooter visible={hovering}>
                    {selectedDeviceId && deviceIds.length > 1 && (
                        <DeviceName
                            className={styles["device-name"]}
                            id={selectedDeviceId}
                        />
                    )}
                    <DeviceSelector
                        selected={selectedDeviceId}
                        deviceIds={deviceIds}
                        onSelect={this.onSelectDevice}
                        fullscreen={fullscreen}
                    />
                </ModuleFooter>
            </Module>
        );
    }

    private onTick = () => {
        this.calculateVisible();
    };

    private onSelectDevice = (deviceId: Uuid) => {
        this.selectedDeviceId = deviceId;
    };

    private get metadata() {
        const results: {
            [deviceId: string]: {
                deviceId: Uuid;
                keys: {
                    [key: string]: {
                        key: string;
                        all: Map<ITags, { time: Date; value: boolean }[]>;
                        current:
                            | { time: Date; value: boolean; tags: ITags }
                            | undefined;
                    };
                };
            };
        } = {};

        pointsByTagValue(this.data).forEach(({ deviceId, points }) =>
            points.forEach(({ time, value: { keys, values }, tags }) =>
                values.forEach((value, i) => {
                    const key = keys[i];
                    results[deviceId] = results[deviceId] || {
                        deviceId,
                        keys: {},
                    };
                    const deviceResult = results[deviceId];
                    deviceResult.keys[key] = deviceResult.keys[key] || {
                        key,
                        all: new Map(),
                    };
                    const keyResult = deviceResult.keys[key];

                    if (!this.visibleKeys.has(key)) {
                        return;
                    }

                    let tagPoints = keyResult.all.get(tags);
                    if (!tagPoints) {
                        tagPoints = [];
                        keyResult.all.set(tags, tagPoints);
                    }
                    tagPoints.push({ time, value });

                    if (
                        !keyResult.current ||
                        (time.getTime() > keyResult.current.time.getTime() &&
                            time.getTime() <= observeStore.time.getTime())
                    ) {
                        keyResult.current = { time, value, tags };
                    }
                }),
            ),
        );

        return Object.values(results).map(({ deviceId, keys }) => ({
            deviceId,
            keys: Object.values(keys).map(({ key, all, current }) => ({
                key,
                all: [...all.keys()].map(tags => ({
                    tags,
                    points: all.get(tags) || [],
                })),
                current,
            })),
        }));
    }

    private calculateVisible() {
        const container = this.table?.container;
        if (!container) {
            return;
        }
        const trs: HTMLTableRowElement[] = [].slice.call(
            container.querySelectorAll<HTMLTableRowElement>("tr"),
        );
        const visibleKeys = new Set([
            ...trs
                .map(
                    tr =>
                        [
                            tr.querySelector("td:nth-child(2)")?.textContent ||
                                "",
                            tr.offsetTop + tr.offsetHeight >=
                                container.scrollTop &&
                                tr.offsetTop <=
                                    container.scrollTop +
                                        container.offsetHeight,
                        ] as const,
                )
                .filter(([, visible]) => visible)
                .map(([key]) => key),
        ]);
        if (
            visibleKeys.size !== this.visibleKeys.size ||
            [...visibleKeys.values()].some(_ => !this.visibleKeys.has(_))
        ) {
            this.visibleKeys = visibleKeys;
        }
        if (!this.initialized) {
            this.initialized = true;
        }
    }

    private onChangeSize = (size: ModuleSize) => {
        this.size = size;
    };

    private handleResize = () => {
        const header = this.headerRef.current;
        if (!header || !this.initialized) {
            return;
        }
        this.dirtyResize = true;
        const width = header.offsetWidth;
        if (width !== this.columnWidth) {
            this.columnWidth = width;
        }

        if (this.props.onChangeConfiguration && this.props.editingLayout) {
            this.props.onChangeConfiguration({
                streamName: this.props.streamName,
                type: "bitset",
                updates: {
                    columnWidth: width,
                },
            });
        }
    };
}
